/**
 * API client for the identity vault (mounted at /api/identity-vault/).
 *
 * Everything crossing this wire is either bookkeeping or opaque ciphertext —
 * see vaultCrypto.ts for what gets sealed before upload.
 */

import api from './api';
import type {
  VaultAdminConfig,
  VaultAdminHospital,
  VaultItemDto,
  VaultKeys,
  VaultMember,
  VaultStatus,
} from '../types/identityVault';

const BASE = '/identity-vault';

export const getVaultStatus = (): Promise<VaultStatus> => api.get<VaultStatus>(`${BASE}/status/`);

export const enroll = (data: { public_key: string; sealed_private_key: string }): Promise<{ enrolled: boolean }> =>
  api.post<{ enrolled: boolean }>(`${BASE}/enroll/`, data);

export const initializeVault = (data: {
  wrapped_master_key: string;
  recovery_sealed_master_key?: string;
}): Promise<{ initialized: boolean }> => api.post<{ initialized: boolean }>(`${BASE}/initialize/`, data);

export const getMyKeys = (): Promise<VaultKeys> => api.get<VaultKeys>(`${BASE}/keys/`);

export const listVaultItems = (): Promise<{ items: VaultItemDto[] }> =>
  api.get<{ items: VaultItemDto[] }>(`${BASE}/items/`);

export interface VaultItemWriteResult {
  pseudonym: string;
  version: number;
  created?: boolean;
  // Present (with a 409) when an optimistic-concurrency check failed.
  conflict?: boolean;
  current_version?: number;
  ciphertext?: string;
}

/** Write an encrypted identity record. Pass ``expectedVersion`` for the
 *  optimistic-concurrency check, and ``keyVersion`` (the master-key version the
 *  ciphertext was sealed under) for the stale-key guard — a mismatch throws with
 *  the 409 body on ``err.response.data`` (conflict / stale_key). */
export const putVaultItem = (
  pseudonym: string,
  ciphertext: string,
  expectedVersion?: number,
  keyVersion?: number,
): Promise<VaultItemWriteResult> => {
  const body: { ciphertext: string; expected_version?: number; key_version?: number } = { ciphertext };
  if (expectedVersion !== undefined) body.expected_version = expectedVersion;
  if (keyVersion !== undefined) body.key_version = keyVersion;
  return api.put<VaultItemWriteResult>(`${BASE}/items/${pseudonym}/`, body);
};

// ------------------- Admin (hospital selector pattern) -------------------

export const getAdminHospitals = (): Promise<{ hospitals: VaultAdminHospital[]; active_hospital_id: number | null }> =>
  api.get<{ hospitals: VaultAdminHospital[]; active_hospital_id: number | null }>(`${BASE}/admin/hospitals/`);

export const getAdminConfig = (hospitalId?: number): Promise<VaultAdminConfig> =>
  api.get<VaultAdminConfig>(`${BASE}/admin/config/${hospitalId ? `?hospital=${hospitalId}` : ''}`);

export const setIdentityMode = (
  mode: 'REAL' | 'PSEUDONYMOUS',
  hospitalId?: number,
): Promise<{ patient_identity_mode: string; changed: boolean }> =>
  api.put<{ patient_identity_mode: string; changed: boolean }>(
    `${BASE}/admin/config/${hospitalId ? `?hospital=${hospitalId}` : ''}`,
    { patient_identity_mode: mode },
  );

// ------------------- Multi-user grants (Phase 2) -------------------

const hq = (hospitalId?: number) => (hospitalId ? `?hospital=${hospitalId}` : '');

export const getVaultMembers = (hospitalId?: number): Promise<{ members: VaultMember[] }> =>
  api.get<{ members: VaultMember[] }>(`${BASE}/admin/members/${hq(hospitalId)}`);

export const setMemberCapabilities = (
  userId: number,
  caps: { eligible?: boolean; can_grant?: boolean },
  hospitalId?: number,
): Promise<{ user_id: number; eligible: boolean; can_grant: boolean }> =>
  api.patch(`${BASE}/admin/members/${userId}/capabilities/${hq(hospitalId)}`, caps);

export const getMemberPublicKey = (
  userId: number,
  hospitalId?: number,
): Promise<{ user_id: number; public_key: string }> =>
  api.get(`${BASE}/admin/members/${userId}/public-key/${hq(hospitalId)}`);

export const grantMember = (
  userId: number,
  wrappedMasterKey: string,
  keyVersion?: number,
  hospitalId?: number,
): Promise<{ user_id: number; granted: boolean }> =>
  api.post(`${BASE}/admin/members/${userId}/grant/${hq(hospitalId)}`, {
    wrapped_master_key: wrappedMasterKey,
    ...(keyVersion ? { key_version: keyVersion } : {}),
  });

export const revokeMember = (
  userId: number,
  hospitalId?: number,
): Promise<{ user_id: number; revoked: boolean }> =>
  api.post(`${BASE}/admin/members/${userId}/revoke/${hq(hospitalId)}`, {});

export const getActiveMemberKeys = (
  hospitalId?: number,
): Promise<{ members: { user_id: number; public_key: string }[] }> =>
  api.get(`${BASE}/admin/members/active-keys/${hq(hospitalId)}`);

export interface RotatePayload {
  new_master_key_version: number;
  members: { user_id: number; wrapped_master_key: string }[];
  items: { pseudonym: string; ciphertext: string }[];
  recovery_sealed_master_key?: string;
}

export const rotateMasterKey = (
  payload: RotatePayload,
  hospitalId?: number,
): Promise<{ rotated: boolean; master_key_version: number; revoked: number }> =>
  api.post(`${BASE}/admin/rotate/${hq(hospitalId)}`, payload);

// ------------------- Migration wizard (Phase 2) -------------------

export interface MigrationStatus {
  hospital_id: number;
  migration_state: 'NONE' | 'TO_PSEUDONYMOUS' | 'TO_REAL';
  patient_identity_mode: 'REAL' | 'PSEUDONYMOUS';
  vault_initialized: boolean;
  total_patients: number;
  non_pseudonymous_patients: number;
  pseudonymous_patients: number;
}

export type MigrationAction =
  | 'start'
  | 'complete'
  | 'start_reverse'
  | 'complete_reverse'
  | 'abort';

export const getMigrationStatus = (hospitalId?: number): Promise<MigrationStatus> =>
  api.get<MigrationStatus>(`${BASE}/admin/migration/${hq(hospitalId)}`);

export const migrationAction = (
  action: MigrationAction,
  hospitalId?: number,
): Promise<MigrationStatus> =>
  api.post<MigrationStatus>(`${BASE}/admin/migration/${hq(hospitalId)}`, { action });
